"use client";

/**
 * 카테고리 상세 섹션 데이터 타입
 */
interface CategoryDetailData {
  /** 섹션 제목 (예: 지역정주형인재양성) */
  title: string;
  /** 좌측 박스 - 목표 */
  goal: {
    title: string;
    items: string[];
  };
  /** 우측 박스 - 세부 운영 프로그램 */
  programs: {
    title: string;
    items: string[];
  };
  /** 하단 안내 문구 */
  notice?: string;
}

/**
 * 카테고리 상세 섹션 컴포넌트
 * 상위 카테고리 클릭 시 나타나는 상세 정보 표시
 */
interface CategoryDetailSectionProps {
  data: CategoryDetailData;
}

export default function CategoryDetailSection({
  data,
}: CategoryDetailSectionProps) {
  return (
    <div className="w-full py-16 px-4">
      <div className="max-w-[1200px] mx-auto">
        {/* 섹션 제목 */}
        <h2 className="text-2xl font-bold text-[#3436B0] mb-8">{data.title}</h2>

        {/* 두 개의 박스 컨테이너 */}
        <div className="flex gap-6">
          {/* 좌측 박스 - 목표 */}
          <div className="flex-1 rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-[#9184DA]/20 to-[#E91E8C]/10 px-6 py-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-800">{data.goal.title}</h3>
            </div>
            {/* 아이템 리스트 */}
            <ul className="divide-y divide-gray-100">
              {data.goal.items.map((item, idx) => (
                <li
                  key={idx}
                  className="px-6 py-3 text-gray-700 text-sm hover:bg-gray-50 transition-colors"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* 우측 박스 - 세부 운영 프로그램 */}
          <div className="flex-1 rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-[#9184DA]/20 to-[#E91E8C]/10 px-6 py-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-800">{data.programs.title}</h3>
            </div>
            {/* 아이템 리스트 */}
            <ul className="divide-y divide-gray-100">
              {data.programs.items.map((item, idx) => (
                <li
                  key={idx}
                  className="px-6 py-3 text-gray-700 text-sm hover:bg-gray-50 transition-colors"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 하단 안내 문구 */}
        {data.notice && (
          <p className="mt-6 text-sm text-gray-600">{data.notice}</p>
        )}
      </div>
    </div>
  );
}

/**
 * 지역정주형인재양성 상세 데이터
 */
export const regionalTalentData: CategoryDetailData = {
  title: "지역정주형인재양성",
  goal: {
    title: "목표 : 지역 전략산업 경쟁력 강화",
    items: [
      "지역 전략산업 경쟁력 강화",
      "산업별 전문가 참여 실무 중심",
      "첨단 산업기반 산학협력 강화",
      "융복합&글로벌 인재양성",
    ],
  },
  programs: {
    title: "세부 운영 프로그램",
    items: [
      "취업패키지",
      "대학원 산학연계 인재양성 프로그램",
      "표준현장실습",
      "기업맞춤형트랙",
      "캡스톤 디자인",
      "프리칼리지",
    ],
  },
  notice: "기타 세부 운영 프로그램은 사무실로 문의",
};

/**
 * 지산학연협력생태계구축 상세 데이터
 */
export const industryEcosystemData: CategoryDetailData = {
  title: "지산학연협력생태계구축",
  goal: {
    title: "목표 : 혁신적 지역 협력 환류체계",
    items: [
      "수요조사기반(지역, 기업, 기관)",
      "운영평가 및 성과 환류",
      "맞춤형 온오프 컨설팅",
      "성과중심 가치창출",
    ],
  },
  programs: {
    title: "세부 운영 프로그램",
    items: [
      "산학공동기술개발과제",
      "시제품 제작 지원사업",
      "애로기술지도",
      "공용장비활용 교육",
      "창업교육, 창업동아리 육성 지원, 방사광가속기 RISE랩",
    ],
  },
  notice: "기타 세부 운영 프로그램은 사무실로 문의",
};

/**
 * 평생교육체제확대 상세 데이터
 */
export const lifelongEducationData: CategoryDetailData = {
  title: "평생교육체제확대",
  goal: {
    title: "목표 : 지역맞춤형 교육 활성화",
    items: [
      "맞춤형 상시 교육 체계화",
      "창의적 교육과 발굴 사업화",
      "도내기업, 기관 질적 고도화",
    ],
  },
  programs: {
    title: "세부 운영 프로그램",
    items: ["성인 학습자 교육", "지역주민 대상 평생교육"],
  },
  notice: "기타 세부 운영 프로그램은 사무실로 문의",
};

/**
 * 지역현안해결 상세 데이터
 */
export const localIssuesData: CategoryDetailData = {
  title: "지역현안해결",
  goal: {
    title: "목표 : 지역 활력과 글로벌화",
    items: ["지산학연 상생협력 강화", "개방형 혁신생태계 조성", "온라인 협업"],
  },
  programs: {
    title: "세부 운영 프로그램",
    items: [
      "유학생 대상 동아리 교육 지원",
      "유학생 대상 기술 창업 연계 교육",
      "지역현안 분석 및 해결 프로그램",
      "늘봄 교육",
    ],
  },
  notice: "기타 세부 운영 프로그램은 사무실로 문의",
};
